import { QuestionIcon } from '@phosphor-icons/react'
import { useHelpBotContext } from '../../../../context/HelpBotContext'
import { ScoopButton } from '../../../ui/ScoopButton/ScoopButton'
import { ScoopTooltip } from '../../../ui/ScoopTooltip/ScoopTooltip'
import styles from './HelpBotPromptButton.module.scss'

/**
 * HelpBotPromptButton - A button that triggers a specific prompt in the HelpBot
 *
 * Automatically disabled while a response is pending to prevent spam.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.prompt - The prompt text to send to HelpBot when clicked
 * @param {React.ReactNode} [props.children] - Button content (if not provided, shows icon + "Help")
 * @param {'default' | 'outline' | 'outlineGray' | 'ghost' | 'text' | 'icon'} [props.variant='ghost'] - Button style variant
 * @param {'xxs' | 'xs' | 'sm' | 'md'} [props.size='xs'] - Button size
 * @param {boolean} [props.showIcon=true] - Whether to show the question icon
 * @param {boolean} [props.disabled=false] - Manually disable the button (also auto-disabled when response pending)
 * @param {string|React.ReactNode} [props.tooltip] - Tooltip content to show on hover
 * @param {'top' | 'bottom' | 'left' | 'right'} [props.tooltipPlacement='top'] - Tooltip placement
 * @param {number} [props.iconSize] - Custom icon size in pixels (overrides default)
 * @param {boolean} [props.iconMinimal=false] - Minimal icon style: no border, no padding, larger icon (20px)
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles
 *
 * @example
 * // Basic usage with predefined prompt
 * import { HELP_BOT_PROMPTS } from '../../helpBotPrompts'
 * <HelpBotPromptButton prompt={HELP_BOT_PROMPTS.CREATE_CHART} />
 *
 * @example
 * // Custom label
 * <HelpBotPromptButton prompt={HELP_BOT_PROMPTS.CREATE_CHART}>
 *   Need help creating charts?
 * </HelpBotPromptButton>
 *
 * @example
 * // Minimal icon button next to a title
 * <HelpBotPromptButton
 *   prompt="How do I use datasets?"
 *   variant="icon"
 *   iconMinimal
 *   tooltip="Learn how to create and manage datasets"
 * />
 */
export const HelpBotPromptButton = ({
    prompt,
    children,
    variant = 'ghost',
    size = 'xs',
    showIcon = true,
    className = '',
    disabled = false,
    tooltip,
    tooltipPlacement = 'top',
    iconSize,
    iconMinimal = false,
    ...props
}) => {
    const { sendPrompt, isPending } = useHelpBotContext()
    const isDisabled = disabled || isPending

    const handleClick = (e) => {
        e.stopPropagation()
        if (prompt) {
            sendPrompt(prompt)
        }
    }

    const buttonClassName = [
        styles.helpBotPromptButton,
        iconMinimal && styles.iconMinimal,
        className,
    ]
        .filter(Boolean)
        .join(' ')

    // Default icon sizes based on button size
    const getIconSize = () => {
        if (iconSize) return iconSize
        if (iconMinimal) return 20
        return size === 'xxs' ? 14 : size === 'xs' ? 16 : 18
    }

    // Icon-only variant
    if (variant === 'icon') {
        const iconButton = (
            <ScoopButton
                variant="icon"
                size={size}
                className={buttonClassName}
                onClick={handleClick}
                disabled={isDisabled}
                aria-label="Get help"
                {...props}
            >
                <QuestionIcon size={getIconSize()} />
            </ScoopButton>
        )

        if (tooltip) {
            return (
                <ScoopTooltip title={tooltip} placement={tooltipPlacement}>
                    {iconButton}
                </ScoopTooltip>
            )
        }

        return iconButton
    }

    const button = (
        <ScoopButton
            variant={variant}
            size={size}
            leftSection={showIcon ? <QuestionIcon size={size === 'xxs' ? 12 : 14} /> : null}
            className={buttonClassName}
            onClick={handleClick}
            disabled={isDisabled}
            {...props}
        >
            {children || 'Help'}
        </ScoopButton>
    )

    if (tooltip) {
        return (
            <ScoopTooltip title={tooltip} placement={tooltipPlacement}>
                {button}
            </ScoopTooltip>
        )
    }

    return button
}
